import type { Lang } from "@/i18n/translations";

export const BROCHURE_ROUTES: Record<Lang, string> = {
  fr: "/brochure",
  en: "/en/brochure",
  es: "/es/folleto",
};

type Item = { t: string; d: string };

export type BrochureCopy = {
  head: { title: string; description: string; ogLocale: string };
  eyebrow: string;
  h1: string;
  intro: string;
  ctaDevis: string;
  ctaContact: string;
  pdfFr: string;
  pdfEn: string;
  maisonTitle: string;
  maisonText: string;
  chiffres: Item[];
  servicesTitle: string;
  services: Item[];
  flotteTitle: string;
  flotte: Item[];
  promessesTitle: string;
  promesses: Item[];
  destTitle: string;
  destText: string;
  destLink: string;
  contactTitle: string;
  contactText: string;
};

export const BROCHURE: Record<Lang, BrochureCopy> = {
  fr: {
    head: {
      title: "Brochure TSP VIP | Chauffeur privé premium à Paris depuis 2008",
      description: "Découvrez TSP VIP : services de chauffeur privé, flotte premium, engagements et destinations. Brochure en ligne et PDF à télécharger.",
      ogLocale: "fr_FR",
    },
    eyebrow: "Brochure · Maison de chauffeurs privés depuis 2008",
    h1: "TSP VIP, l'art du déplacement premium",
    intro: "Chauffeurs privés pour entreprises, dirigeants et clients VIP à Paris et en Île-de-France, avec des partenaires locaux en France et en Europe.",
    ctaDevis: "Demander un devis",
    ctaContact: "Nous contacter",
    pdfFr: "↓ Brochure PDF (FR)",
    pdfEn: "↓ Brochure PDF (EN)",
    maisonTitle: "La maison",
    maisonText: "Depuis 2008, TSP VIP accompagne dirigeants, assistantes de direction, agences, hôtels et particuliers exigeants. Discrétion, ponctualité et un interlocuteur dédié pour chaque mission.",
    chiffres: [
      { t: "2008", d: "Année de création" },
      { t: "24/7", d: "Réservations" },
      { t: "1 h", d: "Délai de réponse" },
      { t: "7", d: "Passagers en Classe V" },
    ],
    servicesTitle: "Nos services",
    services: [
      { t: "Transferts aéroports & gares", d: "Accueil personnalisé, suivi des vols et des trains." },
      { t: "Mise à disposition", d: "Un chauffeur dédié à l'heure, à la demi-journée ou à la journée." },
      { t: "Chauffeur de direction", d: "Pour dirigeants et COMEX : confidentialité et régularité." },
      { t: "Roadshows & séminaires", d: "Coordination de plusieurs véhicules et programmes multi-arrêts." },
      { t: "Événements", d: "Salons, Fashion Week, sport, concerts : transport organisé de bout en bout." },
      { t: "Longue distance & Europe", d: "Trajets depuis Paris et service via partenaires locaux." },
      { t: "Sprinter VIP", d: "Pour les groupes : salons, séminaires, transferts aéroport en un seul véhicule." },
      { t: "Autocar de prestige", d: "Navettes et grands groupes pour congrès, mariages et événements." },
    ],
    flotteTitle: "La flotte",
    flotte: [
      { t: "Berline", d: "Jusqu'à 3 passagers. Confort et discrétion pour les déplacements d'affaires." },
      { t: "Première", d: "Jusqu'à 3 passagers. Notre catégorie la plus haut de gamme." },
      { t: "Van Classe V", d: "7 passagers : 6 à l'arrière et 1 à côté du chauffeur." },
    ],
    promessesTitle: "Nos engagements",
    promesses: [
      { t: "Réponse sous 1 h", d: "À chaque demande de devis." },
      { t: "Attente aéroport 1 h 30", d: "Incluse à l'aéroport ; 15 min en gare." },
      { t: "Mise à disposition", d: "Minimum 3 h · demi-journée 4 h · journée 8 h." },
      { t: "Annulation gratuite", d: "Jusqu'à 72 h avant la prestation." },
    ],
    destTitle: "Destinations",
    destText: "Service en direct à Paris et en Île-de-France. Cannes, Nice, Monaco, Lyon, Bordeaux, Londres, Bruxelles, Genève, Zurich, Milan, Barcelone, Madrid et Lisbonne via des partenaires locaux, selon disponibilité.",
    destLink: "Voir les destinations",
    contactTitle: "Réserver",
    contactText: "Écrivez-nous ou appelez-nous, 24 h/24 et 7 j/7.",
  },
  en: {
    head: {
      title: "TSP VIP Brochure | Premium private chauffeur in Paris since 2008",
      description: "Discover TSP VIP: private chauffeur services, premium fleet, commitments and destinations. Online brochure and downloadable PDF.",
      ogLocale: "en_GB",
    },
    eyebrow: "Brochure · Private chauffeur house since 2008",
    h1: "TSP VIP, the art of premium travel",
    intro: "Private chauffeurs for companies, executives and VIP clients in Paris and Île-de-France, with local partners across France and Europe.",
    ctaDevis: "Request a quote",
    ctaContact: "Contact us",
    pdfFr: "↓ PDF brochure (FR)",
    pdfEn: "↓ PDF brochure (EN)",
    maisonTitle: "The house",
    maisonText: "Since 2008, TSP VIP has served executives, executive assistants, agencies, hotels and demanding private clients. Discretion, punctuality and one dedicated contact for every mission.",
    chiffres: [
      { t: "2008", d: "Founded" },
      { t: "24/7", d: "Bookings" },
      { t: "1 h", d: "Response time" },
      { t: "7", d: "Passengers in the V-Class" },
    ],
    servicesTitle: "Our services",
    services: [
      { t: "Airport & station transfers", d: "Personal meet & greet, flight and train tracking." },
      { t: "Chauffeur by the hour", d: "A dedicated chauffeur by the hour, half day or full day." },
      { t: "Executive chauffeur", d: "For executives and boards: confidentiality and consistency." },
      { t: "Roadshows & seminars", d: "Multi-vehicle coordination and multi-stop programmes." },
      { t: "Events", d: "Trade shows, Fashion Week, sport, concerts: transport organised end to end." },
      { t: "Long distance & Europe", d: "Trips from Paris and service through local partners." },
      { t: "VIP Sprinter", d: "For groups: trade shows, seminars, airport transfers in a single vehicle." },
      { t: "Prestige coach", d: "Shuttles and large groups for conferences, weddings and events." },
    ],
    flotteTitle: "The fleet",
    flotte: [
      { t: "Sedan", d: "Up to 3 passengers. Comfort and discretion for business travel." },
      { t: "First", d: "Up to 3 passengers. Our most exclusive category." },
      { t: "V-Class van", d: "7 passengers: 6 in the rear and 1 beside the chauffeur." },
    ],
    promessesTitle: "Our commitments",
    promesses: [
      { t: "Reply within 1 h", d: "For every quote request." },
      { t: "1h30 airport waiting", d: "Included at airports; 15 min at stations." },
      { t: "Chauffeur by the hour", d: "Minimum 3 h · half day 4 h · full day 8 h." },
      { t: "Free cancellation", d: "Up to 72 h before the service." },
    ],
    destTitle: "Destinations",
    destText: "Direct service in Paris and Île-de-France. Cannes, Nice, Monaco, Lyon, Bordeaux, London, Brussels, Geneva, Zurich, Milan, Barcelona, Madrid and Lisbon through local partners, subject to availability.",
    destLink: "See destinations",
    contactTitle: "Book",
    contactText: "Write or call us, 24 hours a day, 7 days a week.",
  },
  es: {
    head: {
      title: "Folleto TSP VIP | Chófer privado premium en París desde 2008",
      description: "Descubra TSP VIP: servicios de chófer privado, flota premium, compromisos y destinos. Folleto en línea y PDF descargable.",
      ogLocale: "es_ES",
    },
    eyebrow: "Folleto · Casa de chóferes privados desde 2008",
    h1: "TSP VIP, el arte del desplazamiento premium",
    intro: "Chóferes privados para empresas, directivos y clientes VIP en París e Île-de-France, con socios locales en Francia y Europa.",
    ctaDevis: "Solicitar presupuesto",
    ctaContact: "Contactar",
    pdfFr: "↓ Folleto PDF (FR)",
    pdfEn: "↓ Folleto PDF (EN)",
    maisonTitle: "La casa",
    maisonText: "Desde 2008, TSP VIP acompaña a directivos, asistentes de dirección, agencias, hoteles y clientes particulares exigentes. Discreción, puntualidad y un interlocutor dedicado para cada misión.",
    chiffres: [
      { t: "2008", d: "Año de creación" },
      { t: "24/7", d: "Reservas" },
      { t: "1 h", d: "Plazo de respuesta" },
      { t: "7", d: "Pasajeros en Clase V" },
    ],
    servicesTitle: "Nuestros servicios",
    services: [
      { t: "Traslados aeropuertos y estaciones", d: "Recepción personalizada, seguimiento de vuelos y trenes." },
      { t: "Chófer por horas", d: "Un chófer dedicado por horas, media jornada o jornada completa." },
      { t: "Chófer de dirección", d: "Para directivos y comités: confidencialidad y regularidad." },
      { t: "Roadshows y seminarios", d: "Coordinación de varios vehículos y programas con varias paradas." },
      { t: "Eventos", d: "Ferias, Fashion Week, deporte, conciertos: transporte organizado de principio a fin." },
      { t: "Larga distancia y Europa", d: "Trayectos desde París y servicio a través de socios locales." },
      { t: "Sprinter VIP", d: "Para grupos: ferias, seminarios, traslados al aeropuerto en un solo vehículo." },
      { t: "Autocar de prestigio", d: "Lanzaderas y grandes grupos para congresos, bodas y eventos." },
    ],
    flotteTitle: "La flota",
    flotte: [
      { t: "Berlina", d: "Hasta 3 pasajeros. Confort y discreción para viajes de negocios." },
      { t: "Première", d: "Hasta 3 pasajeros. Nuestra categoría más exclusiva." },
      { t: "Van Clase V", d: "7 pasajeros: 6 detrás y 1 junto al chófer." },
    ],
    promessesTitle: "Nuestros compromisos",
    promesses: [
      { t: "Respuesta en 1 h", d: "Para cada solicitud de presupuesto." },
      { t: "Espera en aeropuerto 1h30", d: "Incluida en aeropuertos; 15 min en estaciones." },
      { t: "Chófer por horas", d: "Mínimo 3 h · media jornada 4 h · jornada 8 h." },
      { t: "Cancelación gratuita", d: "Hasta 72 h antes del servicio." },
    ],
    destTitle: "Destinos",
    destText: "Servicio directo en París e Île-de-France. Cannes, Niza, Mónaco, Lyon, Burdeos, Londres, Bruselas, Ginebra, Zúrich, Milán, Barcelona, Madrid y Lisboa a través de socios locales, según disponibilidad.",
    destLink: "Ver destinos",
    contactTitle: "Reservar",
    contactText: "Escríbanos o llámenos, 24 horas al día, 7 días a la semana.",
  },
};
